import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Trash2, ArrowUp, ArrowDown, X, ListMusic, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { formatDuration } from '../../utils/catalogUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Builder = ({ playlistId, onBack, onChanged }) => {
  const [pl, setPl] = useState(null);
  const [q, setQ] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/catalog/playlists/${playlistId}`, { withCredentials: true });
    setPl(r.data);
  }, [playlistId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setTimeout(async () => {
      const params = {};
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/catalog/tracks`, { params, withCredentials: true });
      setCatalog(r.data.tracks || []);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const inPlaylist = new Set((pl?.tracks || []).map((t) => t.id));

  const add = async (track) => {
    setSaving(true);
    try {
      await axios.post(`${API}/catalog/playlists/${playlistId}/tracks`, { track_id: track.id }, { withCredentials: true });
      await load();
      onChanged?.();
    } catch { toast.error('Could not add track'); } finally { setSaving(false); }
  };

  const removeTrack = async (track) => {
    setSaving(true);
    try {
      await axios.delete(`${API}/catalog/playlists/${playlistId}/tracks/${track.id}`, { withCredentials: true });
      await load();
      onChanged?.();
    } catch { toast.error('Could not remove track'); } finally { setSaving(false); }
  };

  const reorder = async (idx, dir) => {
    const ids = (pl.tracks || []).map((t) => t.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setSaving(true);
    try {
      await axios.put(`${API}/catalog/playlists/${playlistId}`, { track_ids: ids }, { withCredentials: true });
      await load();
    } catch { toast.error('Reorder failed'); } finally { setSaving(false); }
  };

  if (!pl) return <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="playlist-builder">
      <button type="button" onClick={onBack} className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-1" data-testid="playlist-back">
        <ChevronRight className="w-4 h-4 rotate-180" /> All playlists
      </button>
      <div className="mb-4">
        <h2 className="text-2xl font-extrabold">{pl.name}</h2>
        {pl.description && <p className="text-sm text-gray-400 mt-0.5">{pl.description}</p>}
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{pl.track_count} track{pl.track_count === 1 ? '' : 's'}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Current tracks */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-[#39FF14] font-bold mb-3">In this playlist</p>
          {(pl.tracks || []).length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">Add songs from the catalog on the right.</p>
          ) : (
            <ul className="space-y-1" data-testid="playlist-tracks">
              {pl.tracks.map((t, idx) => (
                <li key={t.id} className="flex items-center gap-2 bg-white/[0.02] rounded-lg px-2 py-1.5" data-testid={`pl-track-${t.id}`}>
                  <span className="text-xs text-gray-600 w-5 text-right">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{t.title}</p>
                    <p className="text-[11px] text-gray-500 truncate">{t.artist || '—'} · {formatDuration(t.duration_ms)}</p>
                  </div>
                  <button type="button" disabled={saving} onClick={() => reorder(idx, -1)} className="text-gray-500 hover:text-white p-1 disabled:opacity-30" data-testid={`pl-up-${t.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button type="button" disabled={saving} onClick={() => reorder(idx, 1)} className="text-gray-500 hover:text-white p-1 disabled:opacity-30" data-testid={`pl-down-${t.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button type="button" disabled={saving} onClick={() => removeTrack(t)} className="text-gray-500 hover:text-red-400 p-1" data-testid={`pl-remove-${t.id}`}><X className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Catalog picker */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-3">Catalog</p>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search songs…" className="pl-9 h-9 bg-black/50 border-white/10" data-testid="builder-search" />
          </div>
          <ul className="space-y-1 max-h-96 overflow-y-auto pr-1" data-testid="builder-catalog">
            {catalog.map((t) => (
              <li key={t.id} className="flex items-center gap-2 bg-white/[0.02] rounded-lg px-2 py-1.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{t.title}</p>
                  <p className="text-[11px] text-gray-500 truncate">{t.artist || '—'}{t.genre ? ` · ${t.genre}` : ''}</p>
                </div>
                <Button size="sm" variant="ghost" disabled={saving || inPlaylist.has(t.id)} onClick={() => add(t)} className="h-7 px-2 text-xs text-[#FF007F] hover:bg-[#FF007F]/10 disabled:opacity-40" data-testid={`builder-add-${t.id}`}>
                  {inPlaylist.has(t.id) ? 'Added' : <><Plus className="w-3.5 h-3.5 mr-1" />Add</>}
                </Button>
              </li>
            ))}
            {catalog.length === 0 && <li className="text-sm text-gray-500 py-6 text-center">No songs match.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const CatalogPlaylistsTab = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/catalog/playlists`, { withCredentials: true });
      setPlaylists(r.data.playlists || []);
    } catch { toast.error('Failed to load playlists'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return toast.error('Name is required');
    setCreating(true);
    try {
      await axios.post(`${API}/catalog/playlists`, { name: name.trim(), description: desc.trim() }, { withCredentials: true });
      setName(''); setDesc('');
      toast.success('Playlist created');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const del = async (pl) => {
    if (!window.confirm(`Delete playlist "${pl.name}"?`)) return;
    try {
      await axios.delete(`${API}/catalog/playlists/${pl.id}`, { withCredentials: true });
      setPlaylists((prev) => prev.filter((p) => p.id !== pl.id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  if (selected) {
    return <Builder playlistId={selected} onBack={() => { setSelected(null); load(); }} onChanged={load} />;
  }

  return (
    <div className="space-y-5" data-testid="catalog-playlists-tab">
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="text-[11px] text-gray-400">Playlist name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dinner Service" className="h-10 bg-black/50 border-white/10 mt-1" data-testid="playlist-name-input" />
        </div>
        <div className="flex-1">
          <label className="text-[11px] text-gray-400">Description (optional)</label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={1} className="bg-black/50 border-white/10 mt-1 min-h-10" data-testid="playlist-desc-input" />
        </div>
        <Button onClick={create} disabled={creating} className="h-10 bg-[#FF007F] hover:bg-[#CC0066] gap-2" data-testid="playlist-create-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : playlists.length === 0 ? (
        <div className="py-16 text-center" data-testid="playlists-empty">
          <ListMusic className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400">No playlists yet. Create one above, then fill it from your catalog.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="playlists-grid">
          {playlists.map((pl) => (
            <div key={pl.id} className="group bg-white/[0.03] border border-white/10 rounded-2xl p-4 hover:border-[#FF007F]/40 transition-colors cursor-pointer" onClick={() => setSelected(pl.id)} data-testid={`playlist-card-${pl.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#FF007F]/15 flex items-center justify-center flex-shrink-0">
                  <ListMusic className="w-5 h-5 text-[#FF007F]" />
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); del(pl); }} className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`playlist-delete-${pl.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-white font-semibold mt-3 truncate">{pl.name}</p>
              {pl.description && <p className="text-xs text-gray-500 truncate mt-0.5">{pl.description}</p>}
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-2">{pl.track_count} track{pl.track_count === 1 ? '' : 's'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
